import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LogOut, ChevronLeft, ChevronRight, HelpCircle } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';

export const BrandMark = ({ size = 36 }) => (
  <div
    className="rounded-xl relative overflow-hidden flex items-center justify-center shrink-0 shadow-sm"
    style={{ width: size, height: size }}
  >
    <span className="absolute inset-0 bg-nav-active" />
    <span className="absolute inset-0 bg-grain" />
    <img
      src="/brand-flame-white.png"
      alt="Jobasti"
      className="relative z-10 object-contain select-none"
      style={{ width: size * 0.6, height: size * 0.6 }}
      draggable={false}
    />
  </div>
);

// Shared pilot-style sidebar contents — white surface, rounded nav pills, deep
// navy active state, a user account popover (View Profile / Settings / Sign
// out), a Support entry and a collapse toggle. Used by both the Recruiter and
// Sales layouts (desktop rail + mobile drawer).
export const SidebarInner = ({
  navItems,
  brand,
  user,
  profileMenu = [],
  onLogout,
  support,
  collapsed = false,
  onToggleCollapse,
  onNavigate,
  ids = {},
}) => {
  const location = useLocation();
  const supportActive = support && (location.pathname === support.path || location.pathname.startsWith(support.path + '/'));

  return (
    <div className="flex flex-col h-full bg-white border-r border-slate-200">
      {/* Brand */}
      <div className={`px-4 py-5 flex items-center ${collapsed ? 'justify-center px-3' : ''}`}>
        <Link
          to={brand.homePath}
          onClick={onNavigate}
          className="flex items-center gap-3 min-w-0"
          data-testid={ids.brand}
        >
          <BrandMark />
          {!collapsed && (
            <div className="min-w-0">
              <div className="font-display font-bold text-lg tracking-tight text-slate-900 leading-none notranslate" translate="no">
                Jobasti<span className="text-blue-600">IQ</span>
              </div>
              {brand.tagline && (
                <div className="text-[10px] text-slate-400 mt-1 truncate uppercase tracking-[0.18em]" data-testid={ids.tagline}>
                  {brand.tagline}
                </div>
              )}
            </div>
          )}
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-1 space-y-1 overflow-y-auto sidebar-scroll" data-testid={ids.nav}>
        {navItems.map(({ path, label, icon: Icon }) => {
          const active = location.pathname === path || location.pathname.startsWith(path + '/');
          return (
            <Link
              key={path}
              to={path}
              onClick={onNavigate}
              title={collapsed ? label : undefined}
              className={`relative overflow-hidden flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm transition-all ${
                active
                  ? 'text-white font-semibold shadow-sm'
                  : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900 font-medium'
              } ${collapsed ? 'justify-center px-2' : ''}`}
              data-testid={`${ids.navPrefix}-${label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {active && (
                <>
                  <span className="absolute inset-0 bg-nav-active" />
                  <span className="absolute inset-0 bg-grain" />
                </>
              )}
              <Icon size={18} strokeWidth={1.9} className="shrink-0 relative z-10" />
              {!collapsed && <span className="truncate relative z-10">{label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* Footer: user popover + support + collapse */}
      <div className="p-3 space-y-1 border-t border-slate-200">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              title={collapsed ? user.displayName : undefined}
              className={`w-full flex items-center gap-2.5 rounded-xl px-2 py-2 hover:bg-slate-100 transition ${collapsed ? 'justify-center px-2' : ''}`}
              data-testid={ids.profile}
            >
              <img src={user.avatarUrl} alt="" className="w-8 h-8 rounded-full object-cover ring-2 ring-slate-100 shrink-0" />
              {!collapsed && (
                <div className="min-w-0 text-left flex-1">
                  <div className="text-sm font-semibold text-slate-900 truncate">{user.displayName}</div>
                  <div className="text-[11px] text-blue-600 truncate">{user.roleLabel}</div>
                </div>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="top" className="w-56" data-testid={ids.profileMenu}>
            <DropdownMenuLabel>
              <div className="font-semibold text-slate-900 truncate">{user.displayName}</div>
              {user.email && <div className="text-xs font-normal text-slate-400 truncate">{user.email}</div>}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {profileMenu.map((item) => {
              const ItemIcon = item.icon;
              return (
                <DropdownMenuItem
                  key={item.label}
                  onClick={item.onClick}
                  className="cursor-pointer"
                  data-testid={item.testId}
                >
                  {ItemIcon && <ItemIcon size={14} className="mr-2 text-slate-500" />} {item.label}
                </DropdownMenuItem>
              );
            })}
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-red-600 cursor-pointer" onClick={onLogout} data-testid={ids.logout}>
              <LogOut size={14} className="mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {support && (
          <Link
            to={support.path}
            onClick={onNavigate}
            title={collapsed ? 'Support' : undefined}
            className={`relative overflow-hidden flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm transition-all ${
              supportActive
                ? 'text-white font-semibold shadow-sm'
                : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900 font-medium'
            } ${collapsed ? 'justify-center px-2' : ''}`}
            data-testid={ids.support}
          >
            {supportActive && (
              <>
                <span className="absolute inset-0 bg-nav-active" />
                <span className="absolute inset-0 bg-grain" />
              </>
            )}
            <span className="relative z-10 shrink-0 flex items-center justify-center">
              <HelpCircle size={18} strokeWidth={1.9} />
              {support.badge > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 bg-rose-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                  {support.badge}
                </span>
              )}
            </span>
            {!collapsed && <span className="truncate relative z-10">Support</span>}
          </Link>
        )}

        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className={`w-full flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition ${
              collapsed ? 'justify-center px-2' : ''
            }`}
            data-testid={ids.collapse}
          >
            {collapsed ? (
              <ChevronRight size={16} className="shrink-0" />
            ) : (
              <>
                <ChevronLeft size={14} className="shrink-0" />
                <span>Collapse</span>
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default SidebarInner;
